import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    OnDestroy,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { isEmpty, sort, ascend, descend, prop } from 'ramda';
import { isEqual, isNil } from 'lodash';
import { z } from 'zod';
import { DateTime } from 'luxon';

const ContributionSchema = z.object({
    brokerName: z.string().or(z.number()).or(z.null()),
    brokerPaymentType: z.string(),
    carrierName: z.string().or(z.number()).or(z.null()),
    contributorType: z.string(),
    customerName: z.string().or(z.number()).or(z.null()),
    destinationCity: z.string(),
    destinationState: z.string(),
    destinationZipCode: z.string().or(z.number()),
    equipType: z.string(),
    id: z.number(),
    originCity: z.string(),
    originState: z.string(),
    originZipCode: z.string().or(z.number()),
    pickupDate: z.string(),
    shipperName: z.string().or(z.number()).or(z.null()),
    shipperPaymentType: z.string(),
    totalPaidByBroker: z.number(),
    totalPaidByShipper: z.number(),
});

type Contribution = z.infer<typeof ContributionSchema>;

const ContributionsPayloadSchema = z.object({
    id: z.string(),
    errored: z.boolean(),
    BrokerCarrierSpotContributions: z.array(ContributionSchema).optional(),
    ShipperCarrierContractContributions: z.array(ContributionSchema).optional(),
    spotKeys: z.array(z.string()).optional(),
    contractKeys: z.array(z.string()).optional(),
});

const RateTypePayloadSchema = z.object({
    id: z.string(),
    rateType: z.string(),
});

const TripTypePayloadSchema = z.object({
    id: z.string(),
    perLoad: z.boolean(),
});

type ContributionsPayload = z.infer<typeof ContributionsPayloadSchema>;
type TripTypePayload = z.infer<typeof TripTypePayloadSchema>;
type RateTypePayload = z.infer<typeof RateTypePayloadSchema>;

const sortingFunctions: Record<
    string,
    (asc: boolean) => (a: Contribution, b: Contribution) => number
> = {
    pickup: (asc: boolean) => (a: Contribution, b: Contribution) => {
        const dateA: number = DateTime.fromFormat(
            a.pickupDate,
            'yyyy-MM-dd',
        ).toMillis();
        const dateB: number = DateTime.fromFormat(
            b.pickupDate,
            'yyyy-MM-dd',
        ).toMillis();

        return asc ? dateA - dateB : dateB - dateA;
    },
    brokerPaidToCarrier:
        (asc: boolean) => (a: Contribution, b: Contribution) => {
            return asc
                ? a.totalPaidByBroker - b.totalPaidByBroker
                : b.totalPaidByBroker - a.totalPaidByBroker;
        },
    brokerReceived: (asc: boolean) => (a: Contribution, b: Contribution) => {
        return asc
            ? a.totalPaidByShipper - b.totalPaidByShipper
            : b.totalPaidByShipper - a.totalPaidByShipper;
    },
    carrierReceivedFromBroker:
        (asc: boolean) => (a: Contribution, b: Contribution) => {
            return asc
                ? a.totalPaidByBroker - b.totalPaidByBroker
                : b.totalPaidByBroker - a.totalPaidByBroker;
        },
    carrierReceivedFromShipper:
        (asc: boolean) => (a: Contribution, b: Contribution) => {
            return asc
                ? a.totalPaidByShipper - b.totalPaidByShipper
                : b.totalPaidByShipper - a.totalPaidByShipper;
        },
    shipperPaidToBroker:
        (asc: boolean) => (a: Contribution, b: Contribution) => {
            return asc
                ? a.totalPaidByShipper - b.totalPaidByShipper
                : b.totalPaidByShipper - a.totalPaidByShipper;
        },
    shipperPaidToCarrier:
        (asc: boolean) => (a: Contribution, b: Contribution) => {
            return asc
                ? a.totalPaidByShipper - b.totalPaidByShipper
                : b.totalPaidByShipper - a.totalPaidByShipper;
        },
    originDestination: (asc: boolean) => {
        return asc ? ascend(prop('originCity')) : descend(prop('originCity'));
    },
    broker: (asc: boolean) => (a: Contribution, b: Contribution) => {
        const brokerA = a.brokerName ?? '';
        const brokerB = b.brokerName ?? '';
        const num: number = brokerA > brokerB ? 1 : brokerA < brokerB ? -1 : 0;
        const multiplier: number = asc ? 1 : -1;

        return num * multiplier;
    },
    shipper: (asc: boolean) => (a: Contribution, b: Contribution) => {
        const brokerA = a.shipperName ?? '';
        const brokerB = b.shipperName ?? '';
        const num: number = brokerA > brokerB ? 1 : brokerA < brokerB ? -1 : 0;
        const multiplier: number = asc ? 1 : -1;

        return num * multiplier;
    },
    carrier: (asc: boolean) => (a: Contribution, b: Contribution) => {
        const brokerA = a.carrierName ?? '';
        const brokerB = b.carrierName ?? '';
        const num: number = brokerA > brokerB ? 1 : brokerA < brokerB ? -1 : 0;
        const multiplier: number = asc ? 1 : -1;

        return num * multiplier;
    },
};

@Component({
    selector: 'iq-table-contributions-table',
    standalone: true,
    imports: [CommonModule, CdkTableModule, MatIconModule],
    templateUrl: './iq-table-contributions-table.component.html',
    styleUrl: './iq-table-contributions-table.component.scss',
})
export class IqTableContributionsTableComponent {
    id: InputSignal<string> = input();

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    private _deregisterMap: Record<string, IqEventBusBrokerHandler> = {};

    contributionsPayload: WritableSignal<ContributionsPayload> = signal({});
    rateTypePayload: WritableSignal<RateTypePayload> = signal({});
    tripTypePayload: WritableSignal<TripTypePayload> = signal({});

    rateType: Signal<string> = computed(() => this.rateTypePayload().rateType, {
        equal: isEqual,
    });
    loaded: Signal<boolean> = computed(
        () => !isEmpty(this.contributionsPayload()),
        {
            equal: isEqual,
        },
    );
    sort: WritableSignal<{ column: string; asc: boolean }> = signal({
        column: 'pickup',
        asc: false,
    });
    errored: Signal<boolean> = computed(
        () =>
            this.contributionsPayload().errored ||
            isNil(
                this.contributionsPayload()[`${this.rateType()}Contributions`],
            ) ||
            this.contributionsPayload()[`${this.rateType()}Contributions`]
                .length === 0,
        {
            equal: isEqual,
        },
    );
    errorMessage: Signal<string> = computed(
        () => {
            return this.contributionsPayload().errored
                ? 'My Company Reports is currently not available. Please refresh or try again later.'
                : this.contributionsPayload()[`${this.rateType()}Contributions`]
                      .length === 0
                ? `There are no company reports for ${
                      this.rateType() === 'BrokerCarrierSpot'
                          ? 'spot'
                          : 'contract'
                  } rates on this lane.`
                : '';
        },
        {
            equal: isEqual,
        },
    );
    contributions: Signal<any[]> = computed(
        () => {
            const sortFn =
                sortingFunctions[this.sort().column] ?? (() => () => 0);
            return sort(
                sortFn(this.sort().asc),
                this.contributionsPayload()[`${this.rateType()}Contributions`],
            );
        },
        {
            equal: isEqual,
        },
    );

    displayedColumns: Signal<string[]> = computed(
        () => this.contributionsPayload()[`${this.rateType()}Keys`],
    );
    colsClasses: Signal<{ gridCols: string; colSpan: string }> = computed(
        () => {
            return {
                gridCols: `grid-cols-${this.displayedColumns().length}`,
                colSpan: `col-span-${this.displayedColumns().length}`,
            };
        },
        {
            equal: isEqual,
        },
    );

    private trackingEventEmitted: boolean = false;

    constructor() {
        const handlerOpts = {
            parentId: this.id(),
        };
        this._deregisterMap = this._eventBus.registerMap({
            setContributionsTableContributions: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    if (!this.rateType()) return;
                    const test: any =
                        ContributionsPayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'setContributionsTableContrib Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }
                    this.contributionsPayload.set(payload);
                    if (this.loaded()) {
                        if (!this.trackingEventEmitted) {
                            // Josh Newsom - this flag and the timeout to clear it are bandaids to prevent this event from firing multiple times when the table loads
                            // this is not a great solution and eventually we should track down the source of the multiple events
                            // and fix the issue there
                            this.trackingEventEmitted = true;

                            setTimeout(() => {
                                this.trackingEventEmitted = false;
                            }, 500);

                            this._eventBus.dispatch({
                                name: 'rateviewLoadedContributions',
                                payload: {
                                    id: this.id(),
                                },
                            });
                        }

                    }
                },
                opts: {
                    ...handlerOpts,
                },
            },
            setContributionsTableRateType: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    const test: any = RateTypePayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'setContributionsTableRateType Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }
                    this.rateTypePayload.set(payload);
                },
                opts: {
                    ...handlerOpts,
                },
            },
            setContributionsTableTripType: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    const test: any = TripTypePayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'setContributionsTableTripType Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }
                    this.tripTypePayload.set(payload);
                },
                opts: {
                    ...handlerOpts,
                },
            },
        });

        effect(
            () => {
                const contributionsPayload = this.contributionsPayload();
                const rateType: string = this.rateType();

                if (!isEmpty(contributionsPayload) && !!rateType) {
                    const hasError: boolean = contributionsPayload.errored;
                    const label: string = `${
                        contributionsPayload[`${rateType}Contributions`].length
                    }`;
                    const currentColumn: string = this.sort().column;

                    this._eventBus.dispatch({
                        name: 'type:rate-fetch:Contributions:set-label',
                        payload: {
                            hasError,
                            label,
                            resetSort: currentColumn !== 'pickup',
                        },
                    });
                }
            },
            { allowSignalWrites: true },
        );
    }

    ngOnDestroy() {
        this._eventBus.unregisterMap(this._deregisterMap);
    }

    setSort(column: string) {
        const resetDir: boolean = column !== this.sort().column; // whether to reset the direction to "descending" (false); (orr.epstein)
        const opp: boolean = !this.sort().asc;
        this.sort.set({
            column,
            asc: !resetDir && opp,
        });

        this._eventBus.dispatch({
            name: 'rateviewTrackingSortedMyCompanyReports',
            payload: {
                sort_column: column,
                sort_direction: this.sort().asc ? 'ascending' : 'descending',
            },
        });
    }
}
